import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { env } from "../../config/env";
import { ApiError } from "../../lib/api-client";
import { useAuth } from "../auth/useAuth";
import { getDashboardBundle } from "./api";
import type { DashboardBundle } from "./types";

type DashboardState = {
  data: DashboardBundle | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: string;
};

const initialState: DashboardState = {
  data: null,
  loading: true,
  refreshing: false,
  error: null,
  lastUpdated: "-",
};

export function useDashboardData(siteId?: number | null) {
  const { logout } = useAuth();
  const [state, setState] = useState<DashboardState>(initialState);
  const abortRef = useRef<AbortController | null>(null);
  const unauthorizedHandledRef = useRef(false);

  const handleUnauthorized = useCallback(() => {
    if (unauthorizedHandledRef.current) return;
    unauthorizedHandledRef.current = true;
    setState((prev) => ({
      ...prev,
      loading: false,
      refreshing: false,
      error: "Session expired. Please sign in again.",
    }));
    logout();
  }, [logout]);

  const loadDashboard = useCallback(async (mode: "initial" | "refresh" = "initial") => {
    if (unauthorizedHandledRef.current) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((prev) => ({
      ...prev,
      loading: mode === "initial",
      refreshing: mode === "refresh",
      error: null,
    }));

    try {
      const data = await getDashboardBundle(controller.signal, siteId);
      setState({
        data,
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: new Date().toLocaleTimeString(),
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof ApiError && error.status === 401) {
        handleUnauthorized();
        return;
      }
      setState((prev) => ({
        ...prev,
        loading: false,
        refreshing: false,
        error: error instanceof Error ? error.message : "Failed to load dashboard data",
      }));
    }
  }, [handleUnauthorized, siteId]);

  useEffect(() => {
    void loadDashboard("initial");
    const interval = setInterval(() => {
      void loadDashboard("refresh");
    }, env.dashboardRefreshMs);

    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [loadDashboard]);

  const statsCards = useMemo(
    () => [
      { label: "Today Visits", value: state.data?.stats.today_visits ?? 0 },
      { label: "Inside", value: state.data?.stats.inside_visitors ?? 0 },
      { label: "Overstay", value: state.data?.stats.overstay_visitors ?? 0 },
      { label: "Pending", value: state.data?.alerts.pending_approval_count ?? 0 },
    ],
    [state.data],
  );

  return {
    ...state,
    statsCards,
    refresh: () => loadDashboard("refresh"),
  };
}
